import { supabase } from '../lib/supabase';

class PartsStoreService {
  constructor() {
    this.productsTable = 'store_products';
    this.ordersTable = 'store_orders';
    this.storageBucket = 'product-images';
  }

  // ==================== PRODUCTS ====================

  async getProducts({ category, search, sort, status, page = 1, limit = 24 } = {}) {
    try {
      let query = supabase
        .from(this.productsTable)
        .select('*', { count: 'exact' });

      // Default to active products for public
      if (status) {
        query = query.eq('status', status);
      } else {
        query = query.eq('status', 'active');
      }

      if (category && category !== 'all') {
        query = query.eq('category', category);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%,sku.ilike.%${search}%`);
      }

      // Sorting
      switch (sort) {
        case 'price_low':
          query = query.order('price', { ascending: true });
          break;
        case 'price_high':
          query = query.order('price', { ascending: false });
          break;
        case 'name_az':
          query = query.order('name', { ascending: true });
          break;
        case 'name_za':
          query = query.order('name', { ascending: false });
          break;
        case 'newest':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      // Pagination
      const from = (page - 1) * limit;
      query = query.range(from, from + limit - 1);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        data: data || [],
        total: count || 0,
        page,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (err) {
      console.error('Error fetching products:', err);
      return { data: [], total: 0, page: 1, totalPages: 0 };
    }
  }

  async getAllProducts() {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error fetching all products:', err);
      return { data: [], error: err };
    }
  }

  async getProductBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error fetching product:', err);
      return { data: null, error: err };
    }
  }

  async getProductById(id) {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  async getFeaturedProducts(limit = 8) {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('*')
        .eq('status', 'active')
        .eq('featured', true)
        .limit(limit)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  async getCategories() {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('category')
        .eq('status', 'active');

      if (error) throw error;

      const categories = [...new Set((data || []).map(p => p.category))].sort();
      return { data: categories, error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  async getRelatedProducts(product, limit = 4) {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .select('*')
        .eq('status', 'active')
        .eq('category', product.category)
        .neq('id', product.id)
        .limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  // ==================== ADMIN CRUD ====================

  generateSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 80);
  }

  async createProduct(productData) {
    try {
      const slug = productData.slug || this.generateSlug(productData.name);
      const { data, error } = await supabase
        .from(this.productsTable)
        .insert({
          ...productData,
          slug,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error creating product:', err);
      return { data: null, error: err };
    }
  }

  async updateProduct(id, updates) {
    try {
      if (updates.name && !updates.slug) {
        updates.slug = this.generateSlug(updates.name);
      }
      const { data, error } = await supabase
        .from(this.productsTable)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  async deleteProduct(id) {
    try {
      const { error } = await supabase
        .from(this.productsTable)
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  }

  async updateStock(id, quantity) {
    try {
      const { data, error } = await supabase
        .from(this.productsTable)
        .update({
          stock_quantity: quantity,
          status: quantity <= 0 ? 'out_of_stock' : 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // ==================== IMAGE UPLOAD ====================

  async uploadProductImage(file, productId) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${productId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from(this.storageBucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from(this.storageBucket)
        .getPublicUrl(data.path);

      return { url: urlData.publicUrl, error: null };
    } catch (err) {
      console.error('Error uploading image:', err);
      return { url: null, error: err };
    }
  }

  async deleteProductImage(imageUrl) {
    try {
      const path = imageUrl.split(`${this.storageBucket}/`)[1];
      if (!path) return { error: null };

      const { error } = await supabase.storage
        .from(this.storageBucket)
        .remove([path]);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  }

  // ==================== ORDERS ====================

  async generateOrderNumber() {
    const year = new Date().getFullYear();
    const { count } = await supabase
      .from(this.ordersTable)
      .select('*', { count: 'exact', head: true });

    const seq = String((count || 0) + 1).padStart(5, '0');
    return `PP-ORD-${year}-${seq}`;
  }

  async createOrder(orderData) {
    try {
      const orderNumber = await this.generateOrderNumber();
      const { data, error } = await supabase
        .from(this.ordersTable)
        .insert({
          ...orderData,
          order_number: orderNumber,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Decrement stock for each item
      for (const item of orderData.items) {
        const { data: product } = await this.getProductById(item.product_id);
        if (product) {
          await this.updateStock(item.product_id, Math.max(0, product.stock_quantity - item.quantity));
        }
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error creating order:', err);
      return { data: null, error: err };
    }
  }

  async getOrders({ status, page = 1, limit = 25 } = {}) {
    try {
      let query = supabase
        .from(this.ordersTable)
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('payment_status', status);
      }

      const from = (page - 1) * limit;
      query = query.range(from, from + limit - 1);

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: data || [], total: count || 0 };
    } catch (err) {
      return { data: [], total: 0, error: err };
    }
  }

  async updateOrder(id, updates) {
    try {
      const { data, error } = await supabase
        .from(this.ordersTable)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }
}

export const partsStoreService = new PartsStoreService();
export default partsStoreService;
